// src/pages/orders/components/AddDeliveryFollowUpModal.jsx
import React, { useState } from 'react';
import { X, Phone, Save, Loader2 } from 'lucide-react';
import { createDeliveryFollowUpNote } from '../../../services/api/orders';
import styles from './AddDeliveryFollowUpModal.module.css';

const FOLLOW_UP_TYPE_OPTIONS = [
  { value: 'customer_call', label: 'Customer Call' },
  { value: 'courier_call', label: 'Courier Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'status_update', label: 'Status Update' },
  { value: 'internal_note', label: 'Internal Note' }
];

const RESPONSE_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'answered', label: 'Answered' },
  { value: 'not_answered', label: 'Not Answered' },
  { value: 'busy', label: 'Busy' },
  { value: 'switched_off', label: 'Switched Off' },
  { value: 'completed', label: 'Completed' }
];

const AddDeliveryFollowUpModal = ({ orderId, onClose, onSaved }) => {
  const [followUpType, setFollowUpType]     = useState('');
  const [responseStatus, setResponseStatus] = useState('pending');
  const [actionTaken, setActionTaken]       = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [remarks, setRemarks]               = useState('');
  const [isSaving, setIsSaving]             = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleSave = async () => {
    if (!followUpType) {
      setValidationError('Please select a follow-up type');
      return;
    }
    setValidationError('');

    try {
      setIsSaving(true);
      const response = await createDeliveryFollowUpNote(orderId, {
        followUpType,
        responseStatus,
        actionTaken: actionTaken.trim() || null,
        nextFollowUpDate: nextFollowUpDate || null,
        remarks: remarks.trim() || null
      });
      onSaved(response.note);
    } catch (err) {
      console.error('Failed to save delivery follow-up note:', err);
      setValidationError('Failed to save follow-up. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3><Phone size={18} />Add Delivery Follow-up</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>Follow-up Type</label>
              <select value={followUpType} onChange={(e) => setFollowUpType(e.target.value)}>
                <option value="">Select Type</option>
                {FOLLOW_UP_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className={styles.formField}>
              <label>Response Status</label>
              <select value={responseStatus} onChange={(e) => setResponseStatus(e.target.value)}>
                {RESPONSE_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formField}>
            <label>Action Taken</label>
            <input
              type="text"
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              placeholder="Example: Customer requested delivery tomorrow"
            />
          </div>

          <div className={styles.formField}>
            <label>Next Follow-up Date</label>
            <input
              type="datetime-local"
              value={nextFollowUpDate}
              onChange={(e) => setNextFollowUpDate(e.target.value)}
            />
          </div>

          <div className={styles.formField}>
            <label>Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter follow-up remarks..."
              rows={4}
            />
          </div>

          {validationError && <div className={styles.validationError}>{validationError}</div>}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton} disabled={isSaving}>Close</button>
          <button onClick={handleSave} className={styles.saveButton} disabled={isSaving}>
            {isSaving ? <Loader2 size={16} className={styles.spinnerIcon} /> : <Save size={16} />}
            Save Follow-up
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDeliveryFollowUpModal;
